// WebSocket service for real-time messaging

import { Message } from '../types/message';
// StorageService import removed - not currently used
import { emergencyStatusService } from './emergencyStatusService';
import { API_BASE_URL } from './api';
import { fetchWithAuthRetry } from './refreshCoordinator';
import { getWebsocketBaseUrl } from '../utils/urlConfig';
import { DEBUG_CONNECTIVITY, DEBUG_LOGS } from '../utils/debugFlags';
import { store } from '../store';

// Returns true only when Redux says the user is fully authenticated. Reading
// the store directly (rather than threading auth state through every caller)
// keeps the singleton's reconnect/visibility paths from racing past hook-level
// gates after the user logs out or the session is invalidated.
const isAuthValid = (): boolean => {
  const auth = store.getState().auth;
  return !!auth.isAuthenticated && !!auth.user && auth.validationState === 'valid';
};

type MessageHandler = (message: Message) => void;
type ConnectionHandler = (connected: boolean) => void;
type TypingHandler = (data: { userId: number; isTyping: boolean; conversationId: string }) => void;
type GroupUpdateHandler = (data: { action: string; group: any; details?: any }) => void;
type AcknowledgmentHandler = (data: any) => void;
type NotificationHandler = (notification: any) => void;
type EmergencyHandler = (data: { type: string; message: string; reason: string }) => void;

const WEBSOCKET_BASE_URL = getWebsocketBaseUrl().replace(/\/$/, '');
const DEBUG_WS_VERBOSE = DEBUG_LOGS;

class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 1000;
  private isConnecting = false;
  private connectionId = 0; // Track connection attempts
  private messageHandlers: MessageHandler[] = [];
  private connectionHandlers: ConnectionHandler[] = [];
  private typingHandlers: TypingHandler[] = [];
  private groupUpdateHandlers: GroupUpdateHandler[] = [];
  private acknowledgmentHandlers: AcknowledgmentHandler[] = [];
  private notificationHandlers: NotificationHandler[] = [];
  private emergencyHandlers: EmergencyHandler[] = [];
  
  // FIX: Add duplicate notification prevention
  private recentNotifications = new Map<string, number>();
  private readonly DUPLICATE_WINDOW_MS = 3000; // 3 seconds
  
  // Track processed message IDs to prevent duplicate styled notifications
  private processedMessageIds = new Set<string>();
  
  // Emergency shutdown tracking
  private isEmergencyDisconnected = false;
  // Flag to prevent reconnection after explicit disconnect (e.g., logout)
  private isExplicitlyDisconnected = false;
  // Only auto-reconnect if connect() was called at least once by an authenticated context
  private hasEverConnected = false;

  // Heartbeat keepalive to prevent silent connection drops (Cloudflare/Nginx idle timeouts)
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private readonly HEARTBEAT_MS = 30000;

  constructor() {
    this.setupVisibilityHandlers();
  }

  private setupVisibilityHandlers(): void {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (
        document.visibilityState === 'visible' &&
        this.hasEverConnected &&
        !this.isConnected() &&
        !this.isExplicitlyDisconnected &&
        !this.isEmergencyDisconnected &&
        isAuthValid()
      ) {
        this.reconnectAttempts = 0;
        setTimeout(() => {
          if (
            document.visibilityState === 'visible' &&
            !this.isConnected() &&
            !this.isConnecting &&
            isAuthValid()
          ) {
            if (DEBUG_CONNECTIVITY) {
              console.log('[WebSocket] Page became visible, reconnecting...');
            }
            this.connect();
          }
        }, 1000);
      }
    });

    window.addEventListener('online', () => {
      if (
        this.hasEverConnected &&
        !this.isConnected() &&
        !this.isExplicitlyDisconnected &&
        !this.isEmergencyDisconnected &&
        isAuthValid()
      ) {
        if (DEBUG_CONNECTIVITY) {
          console.log('[WebSocket] Network back online, reconnecting...');
        }
        this.reconnectAttempts = 0;
        this.connect();
      }
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
    }, this.HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  async connect(): Promise<void> {
    // [WS-DEBUG] Always-on: identify caller + state at every connect attempt.
    // Strip the first two stack frames (Error + this line) to surface the real caller.
    const callerStack = (new Error('ws-connect-caller').stack || '')
      .split('\n')
      .slice(2, 7)
      .join('\n');
    const auth = store.getState().auth;
    console.log('[WS-DEBUG] chat.connect() called', {
      isAuthenticated: auth.isAuthenticated,
      hasUser: !!auth.user,
      validationState: auth.validationState,
      isConnecting: this.isConnecting,
      isOpen: this.socket?.readyState === WebSocket.OPEN,
      isExplicitlyDisconnected: this.isExplicitlyDisconnected,
      isEmergencyDisconnected: this.isEmergencyDisconnected,
      hasEverConnected: this.hasEverConnected,
      reconnectAttempts: this.reconnectAttempts,
      caller: callerStack,
    });

    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('[WS-DEBUG] chat.connect() bail: already connecting');
      return;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('[WS-DEBUG] chat.connect() bail: already OPEN');
      return;
    }

    // Hard guard: never attempt a connection while Redux says the user isn't
    // authenticated. Even if the backend's ws-token endpoint mints a token
    // against a stale cookie, the open handshake will fail and the reconnect
    // loop will kick in — better to reject up front.
    if (!isAuthValid()) {
      console.log('[WS-DEBUG] chat.connect() bail: not authenticated', auth);
      return;
    }

    // If explicitly disconnected, don't auto-connect (but allow manual connect to reset flag)
    // Reset the flag when connect() is called explicitly (e.g., after login)
    if (this.isExplicitlyDisconnected) {
      this.isExplicitlyDisconnected = false;
    }

    this.hasEverConnected = true;
    this.isConnecting = true;
    this.connectionId++; // Increment connection attempt ID
    const currentConnectionId = this.connectionId;

    if (DEBUG_WS_VERBOSE) {
      console.log('[WebSocket] connect() started', { wsBase: WEBSOCKET_BASE_URL, apiBase: API_BASE_URL });
    }

    try {
      // Get temporary WebSocket token from server
      const wsToken = await this.getWebSocketToken();
      if (!wsToken) {
        if (DEBUG_WS_VERBOSE) {
          console.log('[WebSocket] No token - not connecting. Check cookie and /api/auth/websocket-token/ (e.g. 401 or network).');
        }
        this.isConnecting = false;
        return;
      }

      if (DEBUG_WS_VERBOSE) {
        console.log('[WebSocket] Token received, opening socket');
      }

      // Check if this connection attempt is still valid
      if (currentConnectionId !== this.connectionId) {
        this.isConnecting = false;
        return;
      }

      // P0-C: token travels via Sec-WebSocket-Protocol subprotocol header,
      // NOT in the URL path. Browser JS cannot set arbitrary headers on a
      // WebSocket handshake but it CAN set the subprotocol list, and
      // subprotocols are not written to nginx access logs by default. The
      // backend extracts the token from the `aimepic.token.<value>` entry
      // and echoes the same string back per RFC 6455.
      const socketUrl = `${WEBSOCKET_BASE_URL}/chat/`;
      const subprotocol = `aimepic.token.${wsToken}`;
      if (DEBUG_WS_VERBOSE) {
        console.log('[WebSocket] Connecting to', socketUrl, '(token via subprotocol)');
      }

      // Small delay to prevent WebSocket services from connecting simultaneously
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Single-element subprotocol array works on all browsers including
        // Firefox (was previously special-cased with `[]`, which prevented
        // subprotocol negotiation entirely).
        this.socket = new WebSocket(socketUrl, [subprotocol]);

        this.setupEventListeners();
        // Store connection ID for this socket instance
        (this.socket as any)._connectionId = currentConnectionId;
      } catch (error) {
        if (DEBUG_WS_VERBOSE) {
          console.log('[WebSocket] Error creating WebSocket', error instanceof Error ? error.message : String(error));
        }
        this.isConnecting = false;
        this.notifyConnectionHandlers(false);
        this.handleReconnect();
      }
    } catch (error) {
      this.isConnecting = false;
      this.notifyConnectionHandlers(false);
    }
  }

  private async getWebSocketToken(): Promise<string | null> {
    // Cookie may be HttpOnly (invisible to document.cookie) - still try request with credentials: 'include'
    const hasRefreshTokenVisible = document.cookie.includes('hospital_refresh_token');
    const tokenUrl = `${API_BASE_URL}/auth/websocket-token/`;
    if (DEBUG_WS_VERBOSE) {
      console.log('[WebSocket] Requesting token from', tokenUrl, 'cookie visible in JS:', hasRefreshTokenVisible);
    }

    try {
      // Self-heals an expired access cookie (mobile background resume) via a
      // coordinated refresh + single retry instead of returning null and
      // waiting for the next periodic/visibilitychange reconnect attempt.
      const response = await fetchWithAuthRetry(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // [WS-DEBUG] Always-on: token endpoint result. If backend mints a token
      // here while Redux says not-authed, that's the cookie-vs-Redux mismatch.
      console.log('[WS-DEBUG] chat.getWebSocketToken result', {
        status: response.status,
        ok: response.ok,
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.ws_token;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'network error';
      if (DEBUG_WS_VERBOSE) {
        console.log('[WebSocket] Token request error', msg);
      }
      return null;
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    // Add connection timeout for Firefox
    const connectionTimeout = setTimeout(() => {
      if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
        this.isConnecting = false;
        this.handleReconnect();
      }
    }, 10000); // 10 second timeout

    this.socket.onopen = () => {
      clearTimeout(connectionTimeout);
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      // [WS-DEBUG] Always-on: confirm subprotocol echoed back. If empty,
      // the backend did NOT echo `aimepic.token.<value>` and the browser will
      // close the handshake immediately per RFC 6455.
      console.log('[WS-DEBUG] chat.onopen', {
        protocol: this.socket?.protocol || '(empty — backend did not echo subprotocol)',
        readyState: this.socket?.readyState,
      });
      if (DEBUG_CONNECTIVITY) {
        console.log('[WebSocket] Connected');
      }
      this.startHeartbeat();
      this.notifyConnectionHandlers(true);
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        // Silent error handling
      }
    };

    this.socket.onclose = (event) => {
      clearTimeout(connectionTimeout);
      this.isConnecting = false;
      this.stopHeartbeat();
      // [WS-DEBUG] Always-on: surface close code + reason on every disconnect.
      // Common codes:
      //   1000 = normal close, 1006 = abnormal (no close frame received),
      //   4001 = disclaimer-not-accepted (P0-A), 4002 = security violation,
      //   4003 = emergency shutdown.
      console.log('[WS-DEBUG] chat.onclose', {
        code: event.code,
        reason: event.reason || '(none)',
        wasClean: event.wasClean,
        reconnectAttempts: this.reconnectAttempts,
      });
      if (DEBUG_CONNECTIVITY) {
        console.log('[WebSocket] Disconnected', 'code=', event.code, 'reason=', event.reason || '(none)');
      }

      // Handle emergency shutdown disconnections
      if (event.code === 4003) {
        this.isEmergencyDisconnected = true;
        
        // Notify emergency status service
        emergencyStatusService.handleEmergencyDisconnection(
          event.reason || 'WebSocket closed due to emergency shutdown'
        );
        
        // Notify emergency handlers
        this.notifyEmergencyHandlers({
          type: 'emergency_shutdown',
          message: 'Connection terminated due to emergency shutdown',
          reason: event.reason || 'Emergency maintenance'
        });
        
        // Don't attempt reconnection during emergency shutdown
        this.notifyConnectionHandlers(false);
        return;
      }
      
      // Handle other security violations
      if (event.code === 4001 || event.code === 4002) {
        emergencyStatusService.handleEmergencyDisconnection(
          event.reason || 'Access denied due to security violation'
        );

        this.notifyEmergencyHandlers({
          type: 'security_violation',
          message: 'Connection terminated due to access restrictions',
          reason: event.reason || 'Permission denied'
        });

        this.notifyConnectionHandlers(false);
        return;
      }

      this.notifyConnectionHandlers(false);

      // Firefox specific: Handle connection refused differently
      if (navigator.userAgent.toLowerCase().includes('firefox') && event.code === 1006) {
        setTimeout(() => this.handleReconnect(), 3000);
      } else if (event.code !== 1000) {
        this.handleReconnect();
      }
    };

    this.socket.onerror = (event) => {
      clearTimeout(connectionTimeout);
      this.isConnecting = false;
      console.log('[WS-DEBUG] chat.onerror', { event });
      if (DEBUG_CONNECTIVITY) {
        console.log('[WebSocket] Error');
      }
      this.notifyConnectionHandlers(false);
    };
  }

  private handleMessage(data: any): void {
    // Handle emergency shutdown messages
    if (data.type === 'emergency_shutdown' || data.type === 'security_violation') {
      
      this.isEmergencyDisconnected = true;
      
      // Notify emergency status service
      emergencyStatusService.handleEmergencyDisconnection(
        data.reason || data.message || 'Emergency shutdown activated'
      );
      
      // Notify emergency handlers
      this.notifyEmergencyHandlers({
        type: data.type,
        message: data.message || 'AI services have been shut down',
        reason: data.reason || 'Emergency maintenance'
      });
      
      return;
    }
    
    switch (data.type) {
      case 'message':
        // Check for urgency level and show appropriate notifications
        if (data.message.urgency_level && data.message.urgency_level !== 'routine') {
          // Trigger browser notifications for critical/urgent messages
          // Only show message-specific notifications here, not aggregated ones
          if (data.message.urgency_level === 'critical') {
            this.showCriticalMessageNotification(data.message);
          } else if (data.message.urgency_level === 'urgent') {
            this.showUrgentMessageNotification(data.message);
          }

          // ALSO trigger the styled notification alert for urgent/critical real-time messages
          // This ensures urgent/critical messages get proper visual styling since they now skip aggregation
          // Duplicate check only skips the alert - we always call notifyMessageHandlers so the message appears in chat
          if (typeof window !== 'undefined' && (window as any).showGlobalNotificationAlert) {
            const conversationId = String(data.message.receiver?.id || data.message.sender.id);
            const messageId = String(data.message.id);
            const isDuplicate =
              this.isDuplicateNotification(conversationId, data.message.content || '') ||
              this.processedMessageIds.has(messageId);

            if (!isDuplicate) {
              this.processedMessageIds.add(messageId);
              // Clean up old processed message IDs to prevent memory leaks (keep last 100)
              if (this.processedMessageIds.size > 100) {
                const idsArray = Array.from(this.processedMessageIds);
                for (let i = 0; i < 50; i++) {
                  this.processedMessageIds.delete(idsArray[i]);
                }
              }
              const alertData = {
                id: `msg_${data.message.id}`,
                type: (data.message.group ? 'group_message' : 'message') as 'message' | 'group_message',
                title: data.message.group ? `New message in ${data.message.group.name}` : 'New Message',
                message: data.message.content,
                senderName: data.message.sender?.first_name && data.message.sender?.last_name
                  ? `${data.message.sender.first_name} ${data.message.sender.last_name}`
                  : data.message.sender?.username || 'Someone',
                conversationId: data.message.group ? `group_${data.message.group.id}` : String(data.message.sender.id),
                groupId: data.message.group?.id,
                messageId: String(data.message.id),
                timestamp: Date.now(),
                urgencyLevel: data.message.urgency_level as 'routine' | 'urgent' | 'critical',
                urgencyReason: data.message.urgency_reason,
                forceSound: true
              };
              (window as any).showGlobalNotificationAlert(alertData);
            }
          }
          // Propagate urgent/critical messages to notification handlers so the
          // notification bell and notification center capture them in real-time.
          const msgId = String(data.message.id);
          if (!this.processedMessageIds.has(`notif_${msgId}`)) {
            this.processedMessageIds.add(`notif_${msgId}`);

            const senderName = data.message.sender?.first_name && data.message.sender?.last_name
              ? `${data.message.sender.first_name} ${data.message.sender.last_name}`
              : data.message.sender?.username || 'Someone';
            const urgencyLabel = data.message.urgency_level === 'critical' ? 'Critical' : 'Urgent';
            const convId = data.message.group
              ? `group_${data.message.group.id}`
              : String(data.message.sender?.id || '');

            this.notifyNotificationHandlers({
              id: `msg_notif_${data.message.id}`,
              type: 'urgent_message',
              priority: data.message.urgency_level === 'critical' ? 'critical' : 'high',
              title: `${urgencyLabel} Message from ${senderName}`,
              body: data.message.content || '',
              preview: (data.message.content || '').substring(0, 100),
              sender_name: senderName,
              sender_role: data.message.sender?.role || '',
              is_read: false,
              created_at: new Date().toISOString(),
              conversation_id: convId,
              message_id: msgId,
              content: {
                title: `${urgencyLabel} Message from ${senderName}`,
                body: data.message.content || '',
                preview: (data.message.content || '').substring(0, 100),
                attachments: []
              },
              metadata: {
                conversationId: convId,
                messageId: msgId,
                urgencyLevel: data.message.urgency_level,
                urgencyReason: data.message.urgency_reason || ''
              },
              sender: {
                id: String(data.message.sender?.id || ''),
                name: senderName,
                role: data.message.sender?.role || '',
                department: data.message.sender?.department || ''
              }
            });
          }
        }
        // Always notify message handlers so the message appears in chat (recipient and sender)
        this.notifyMessageHandlers(data.message);
        break;
      case 'message_confirmation':
        // Tag as API source since this replaces optimistic message
        // FIXED: DO NOT trigger urgent notifications for confirmations (sender feedback only)
        // Confirmations are meant for optimistic UI updates, not notifications
        const confirmedMessage = { ...data.message, _source: 'api', _isConfirmation: true };
        this.notifyMessageHandlers(confirmedMessage);
        break;
      case 'typing':
        this.notifyTypingHandlers(data);
        break;
      case 'group_update':
        if (DEBUG_WS_VERBOSE) {
          console.log('[WebSocket] Group update received:', data.data);
          console.log('[WebSocket] Notifying', this.groupUpdateHandlers.length, 'group update handlers');
        }
        this.notifyGroupUpdateHandlers(data.data);
        if (DEBUG_WS_VERBOSE) {
          console.log('[WebSocket] Group update handlers notified');
        }
        break;
      case 'acknowledgment':
        this.notifyAcknowledgmentHandlers(data);
        break;
      case 'acknowledgment_deleted':
        this.notifyAcknowledgmentHandlers(data);
        break;
      case 'bulk_acknowledgments':
        this.notifyAcknowledgmentHandlers(data);
        break;
      case 'aggregated_notification':
        this.handleAggregatedNotification(data.notification);
        break;
      case 'notification_update':
        this.notifyNotificationHandlers(data.notification);
        break;
      case 'notification_bulk_update':
        data.notifications?.forEach((notification: any) => {
          this.notifyNotificationHandlers(notification);
        });
        break;
      case 'pong':
      case 'message_received':
        break;
      case 'error':
        break;
      default:
        break;
    }
  }

  private handleAggregatedNotification(notification: any): void {
    // FIX: Check for duplicate notifications first
    const conversationId = notification.metadata?.conversationId || notification.conversation_id || 'unknown';
    const messageContent = notification.last_message_preview || notification.content?.preview || notification.content?.body || '';

    if (this.isDuplicateNotification(conversationId, messageContent)) {
      return;
    }

    // Standardize priority level to urgency level mapping
    const priorityToUrgency: { [key: string]: 'routine' | 'urgent' | 'critical' } = {
      'critical': 'critical',
      'high': 'urgent', 
      'medium': 'routine',
      'low': 'routine'
    };
    
    // Extract urgency level from priority or metadata
    let urgencyLevel: 'routine' | 'urgent' | 'critical' = 'routine';
    if (notification.priority && notification.priority.level) {
      const priorityLevel = notification.priority.level as string;
      urgencyLevel = priorityToUrgency[priorityLevel] || 'routine';
    } else if (notification.metadata && notification.metadata.urgencyLevel) {
      urgencyLevel = notification.metadata.urgencyLevel;
    }
    
    // Import notification handling from the notification hook - but only for actual conversations
    if (typeof window !== 'undefined' && (window as any).showGlobalNotificationAlert) {
      // Extract conversation ID from the raw notification
      let conversationId = notification.metadata?.conversationId || notification.conversation_id;
      
      // Convert backend format to frontend format for direct messages
      if (conversationId && conversationId.startsWith('direct_')) {
        // Extract the other user's ID from direct_27_29 format
        const parts = conversationId.replace('direct_', '').split('_');
        if (parts.length === 2) {
          // For now, use the second user ID (will be fixed properly by serializer)
          conversationId = parts[1];
          
        }
      }
      
      // Skip test conversations that don't have proper navigation
      if (conversationId && conversationId.startsWith('test_')) {
        
        return;
      }
      
      // Extract content from the raw notification (before serialization)
      const alertData = {
        id: notification.id,
        type: 'message' as 'message' | 'group_message',
        title: notification.title || notification.content?.title || 'New Message',
        message: notification.last_message_preview || notification.content?.preview || notification.content?.body || '',
        senderName: notification.sender_name || notification.sender?.name || 'System',
        conversationId: conversationId,
        groupId: notification.metadata?.groupId ? parseInt(notification.metadata.groupId) : undefined,
        timestamp: Date.now(),
        urgencyLevel: urgencyLevel as 'routine' | 'urgent' | 'critical',
        urgencyReason: notification.priority?.reason || notification.metadata?.urgencyReason
      };
      
      (window as any).showGlobalNotificationAlert(alertData);
      
    }
    
    // Show browser notifications for urgent/critical aggregated notifications
    // ONLY for system/AI notifications, not for direct messages (which are handled in message_received)
    const isSystemNotification = notification.sender?.name === 'System' || 
                                notification.sender?.name === 'AI Assistant' ||
                                notification.type === 'system' ||
                                !notification.conversation_id;
    
    if (isSystemNotification) {
      
      if (urgencyLevel === 'urgent') {
        this.showUrgentAggregatedNotification(notification);
      } else if (urgencyLevel === 'critical') {
        this.showCriticalAggregatedNotification(notification);
      }
    } else {
      
    }
  }

  private isDuplicateNotification(conversationId: string, messageContent: string): boolean {
    const key = `${conversationId}-${messageContent.substring(0, 50)}`;
    const now = Date.now();
    const lastShown = this.recentNotifications.get(key);
    
    if (lastShown && (now - lastShown) < this.DUPLICATE_WINDOW_MS) {
      return true; // Duplicate detected
    }
    
    this.recentNotifications.set(key, now);
    
    // Cleanup old entries to prevent memory leaks
    if (this.recentNotifications.size > 100) {
      const cutoff = now - this.DUPLICATE_WINDOW_MS;
      const keysToDelete: string[] = [];
      this.recentNotifications.forEach((timestamp, notifKey) => {
        if (timestamp < cutoff) {
          keysToDelete.push(notifKey);
        }
      });
      keysToDelete.forEach(key => this.recentNotifications.delete(key));
    }
    
    return false;
  }

  private handleReconnect(): void {
    // [WS-DEBUG] Always-on: log every reconnect decision.
    const auth = store.getState().auth;
    console.log('[WS-DEBUG] chat.handleReconnect()', {
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      isExplicitlyDisconnected: this.isExplicitlyDisconnected,
      isEmergencyDisconnected: this.isEmergencyDisconnected,
      authValid: isAuthValid(),
      authState: { isAuthenticated: auth.isAuthenticated, hasUser: !!auth.user, validationState: auth.validationState },
    });

    // Don't attempt reconnection if we were disconnected due to emergency shutdown
    if (this.isEmergencyDisconnected) {
      console.log('[WS-DEBUG] chat.handleReconnect bail: emergency disconnected');
      return;
    }

    // Don't attempt reconnection if explicitly disconnected (e.g., after logout)
    if (this.isExplicitlyDisconnected) {
      console.log('[WS-DEBUG] chat.handleReconnect bail: explicitly disconnected');
      return;
    }

    // Don't burn reconnect attempts when the user is not authenticated. Without
    // this guard, a stale cookie that briefly mints a ws_token can keep the
    // singleton looping after the user has logged out / session was invalidated.
    if (!isAuthValid()) {
      console.log('[WS-DEBUG] chat.handleReconnect bail: not authenticated');
      this.reconnectAttempts = 0;
      return;
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectInterval * this.reconnectAttempts;
      console.log('[WS-DEBUG] chat.handleReconnect scheduling reconnect', {
        attempt: this.reconnectAttempts,
        max: this.maxReconnectAttempts,
        delayMs: delay,
      });
      setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.log('[WS-DEBUG] chat.handleReconnect bail: max attempts reached');
    }
  }

  // Reset reconnection counter (called externally when manually reconnecting)
  resetReconnectAttempts(): void {
    this.reconnectAttempts = 0;
    
  }

  sendMessage(
    content: string, 
    receiverId?: number, 
    groupId?: number, 
    urgencyLevel?: 'routine' | 'urgent' | 'critical',
    urgencyReason?: string,
    traceId?: string
  ): void {
    if (DEBUG_WS_VERBOSE) {
      console.log('[WebSocket] sendMessage called:', {
        hasSocket: !!this.socket,
        readyState: this.socket?.readyState,
        isOpen: this.socket?.readyState === WebSocket.OPEN,
        receiverId,
        groupId,
        urgencyLevel,
        content: content.substring(0, 50) + '...',
        traceId
      });
    }
    
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const messageData: any = {
        type: 'message',
        content,
        receiver_id: receiverId,
        group_id: groupId,
        urgency_level: urgencyLevel || 'routine',
        urgency_reason: urgencyReason
      };
      
      // Add trace ID if provided
      if (traceId) {
        messageData._traceId = traceId;
      }
      
      if (DEBUG_WS_VERBOSE) {
        console.log('[WebSocket] Sending message data:', messageData);
      }
      this.socket.send(JSON.stringify(messageData));
      if (DEBUG_WS_VERBOSE) {
        console.log('[WebSocket] Message sent successfully');
      }
    } else {
      if (DEBUG_WS_VERBOSE) {
        console.error('[WebSocket] Cannot send message - socket not ready:', {
          hasSocket: !!this.socket,
          readyState: this.socket?.readyState,
          expectedState: WebSocket.OPEN
        });
      }
    }
  }

  sendTyping(conversationId: string, isTyping: boolean): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const isGroupConversation = conversationId.startsWith('group_');
      const receiverId = isGroupConversation ? undefined : parseInt(conversationId);
      const groupId = isGroupConversation ? parseInt(conversationId.replace('group_', '')) : undefined;
      
      this.socket.send(JSON.stringify({
        type: 'typing',
        receiver_id: receiverId,
        group_id: groupId,
        is_typing: isTyping
      }));
    }
  }

  onMessage(handler: MessageHandler): void {
    this.messageHandlers.push(handler);
  }

  onConnection(handler: ConnectionHandler): () => void {
    if (DEBUG_WS_VERBOSE) {
      console.log('[WebSocket] onConnection() called, adding handler');
      console.log('[WebSocket] Current state - isConnecting:', this.isConnecting, 'isConnected:', this.isConnected(), 'handlers count:', this.connectionHandlers.length);
    }

    this.connectionHandlers.push(handler);
    if (DEBUG_WS_VERBOSE) {
      console.log('[WebSocket] Handler added, new count:', this.connectionHandlers.length);
    }

    // Immediately call handler with current connection state ONLY if we're not currently connecting
    // If we're connecting, wait for the connection to complete before notifying
    if (!this.isConnecting) {
      const currentState = this.isConnected();
      if (DEBUG_WS_VERBOSE) {
        console.log('[WebSocket] Not currently connecting, will call handler immediately with state:', currentState);
      }
      setTimeout(() => {
        if (DEBUG_WS_VERBOSE) {
          console.log('[WebSocket] Calling handler via setTimeout with state:', currentState);
        }
        handler(currentState);
      }, 0); // Use setTimeout to avoid immediate call during registration
    } else {
      if (DEBUG_WS_VERBOSE) {
        console.log('[WebSocket] Currently connecting, handler will be called when connection completes');
      }
    }

    // Return unsubscribe function
    return () => {
      const index = this.connectionHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionHandlers.splice(index, 1);
      }
    };
  }

  onTyping(handler: TypingHandler): void {
    this.typingHandlers.push(handler);
  }

  onGroupUpdate(handler: GroupUpdateHandler): void {
    this.groupUpdateHandlers.push(handler);
  }

  onAcknowledgment(handler: AcknowledgmentHandler): void {
    this.acknowledgmentHandlers.push(handler);
  }

  onNotification(handler: NotificationHandler): () => void {
    this.notificationHandlers.push(handler);
    
    // Return unsubscribe function
    return () => {
      const index = this.notificationHandlers.indexOf(handler);
      if (index > -1) {
        this.notificationHandlers.splice(index, 1);
      }
    };
  }

  onEmergency(handler: EmergencyHandler): () => void {
    this.emergencyHandlers.push(handler);
    
    // Return unsubscribe function
    return () => {
      const index = this.emergencyHandlers.indexOf(handler);
      if (index > -1) {
        this.emergencyHandlers.splice(index, 1);
      }
    };
  }

  getIsEmergencyDisconnected(): boolean {
    return this.isEmergencyDisconnected;
  }

  clearEmergencyStatus(): void {
    
    this.isEmergencyDisconnected = false;
  }

  clearHandlers(): void {
    this.messageHandlers = [];
    this.connectionHandlers = [];
    this.typingHandlers = [];
    this.groupUpdateHandlers = [];
    this.acknowledgmentHandlers = [];
    this.notificationHandlers = [];
    this.emergencyHandlers = [];
  }

  private notifyMessageHandlers(message: Message): void {
    this.messageHandlers.forEach(handler => handler(message));
  }

  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach(handler => handler(connected));
  }

  private notifyTypingHandlers(data: any): void {
    this.typingHandlers.forEach(handler => handler(data));
  }

  private notifyGroupUpdateHandlers(data: any): void {
    this.groupUpdateHandlers.forEach(handler => handler(data));
  }

  private notifyAcknowledgmentHandlers(data: any): void {
    this.acknowledgmentHandlers.forEach(handler => handler(data));
  }

  private notifyNotificationHandlers(notification: any): void {
    this.notificationHandlers.forEach(handler => handler(notification));
  }

  private notifyEmergencyHandlers(data: { type: string; message: string; reason: string }): void {
    
    this.emergencyHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        
      }
    });
  }

  private showUrgentMessageNotification(message: Message): void {
    // Show browser notification for urgent messages using notification service
    // This will respect page visibility and user preferences
    const notificationService = require('./notification').default;
    const senderName = message.sender?.first_name && message.sender?.last_name 
      ? `${message.sender.first_name} ${message.sender.last_name}`
      : message.sender?.username || 'Someone';
    
    notificationService.showNotification(`ΓÜá∩╕Å URGENT: ${senderName}`, {
      body: `${message.content?.substring(0, 100) + (message.content?.length > 100 ? '...' : '')}${message.urgency_reason ? `\n\nReason: ${message.urgency_reason}` : ''}`,
      icon: '/logo192.png',
      tag: `urgent-${message.id}`,
      requireInteraction: true,
      silent: false
    });
    
  }

  private showCriticalMessageNotification(message: Message): void {
    // Show browser notification for critical messages using notification service
    // This will respect page visibility and user preferences
    const notificationService = require('./notification').default;
    const senderName = message.sender?.first_name && message.sender?.last_name 
      ? `${message.sender.first_name} ${message.sender.last_name}`
      : message.sender?.username || 'Someone';
    
    notificationService.showNotification(`≡ƒÜ¿ CRITICAL: ${senderName}`, {
      body: `${message.content?.substring(0, 100) + (message.content?.length > 100 ? '...' : '')}${message.urgency_reason ? `\n\nReason: ${message.urgency_reason}` : ''}`,
      icon: '/logo192.png',
      tag: `critical-${message.id}`,
      requireInteraction: true,
      silent: false,
      vibrate: [500, 200, 500, 200, 500] // Critical vibration pattern
    });
    
    // For critical messages, also try to play a sound if possible
    try {
      const audio = new Audio('/notification-sound.mp3'); // Add notification sound file
      audio.volume = 0.8; // Higher volume for critical messages
      audio.play().catch(e => {
        
      });
    } catch (e) {
      
    }
    
  }

  private showUrgentAggregatedNotification(notification: any): void {
    // Show browser notification for urgent aggregated notifications using notification service
    const notificationService = require('./notification').default;
    const senderName = notification.sender?.name || 'System';
    
    notificationService.showNotification(`≡ƒƒá Urgent: ${senderName}`, {
      body: notification.content?.preview || notification.content?.body || '',
      icon: '/logo192.png',
      tag: `urgent-aggregated-${notification.id}`,
      requireInteraction: true
    }, true); // Force show for urgent system notifications
    
  }

  private showCriticalAggregatedNotification(notification: any): void {
    // Show browser notification for critical aggregated notifications using notification service
    const notificationService = require('./notification').default;
    const senderName = notification.sender?.name || 'System';
    
    notificationService.showNotification(`≡ƒö┤ CRITICAL: ${senderName}`, {
      body: notification.content?.preview || notification.content?.body || '',
      icon: '/logo192.png',
      tag: `critical-aggregated-${notification.id}`,
      requireInteraction: true,
      vibrate: [500, 200, 500, 200, 500] // Critical vibration pattern
    }, true); // Force show for critical system notifications
    
    // For critical messages, also try to play a sound if possible
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.8;
      audio.play().catch(e => {
        
      });
    } catch (e) {
      
    }
    
  }

  disconnect(): void {
    this.isExplicitlyDisconnected = true;
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
  
  // Reset explicit disconnect flag (call when user logs back in)
  resetDisconnectFlag(): void {
    this.isExplicitlyDisconnected = false;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

const websocketService = new WebSocketService();
export default websocketService;
